import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import {
  Stack,
  Group,
  Card,
  SimpleGrid,
  Text,
  Alert,
  Button,
  Tooltip,
  Box,
  Title,
  Progress,
  Chip,
  ScrollArea,
  Table,
  Badge,
  ActionIcon,
} from '@mantine/core';
import { IconHistory, IconDownload } from '@tabler/icons-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { PeriodFilter } from '../../components/evaluations/PeriodFilter';
import { SkeletonTable } from '../../components/evaluations/SkeletonTable';
import { TransferLinkRow } from '../../components/evaluations/TransferLinkRow';
import { UNFALLDATEN_TYPE } from '../transfers/transferOverviewUrl';
import { accidentEvaluationApi } from '../../services/accidentEvaluationApi';
import { downloadBlob } from '../../utils/downloadBlob';
import type {
  AccidentEvaluationResponse,
  AccidentEvaluationRow,
  AccidentEvaluationRowStatus,
  AccidentEvaluationValues,
} from '../../types/accidentEvaluation';
import { formatNumber, calculateDelta, getDeltaColor, formatPercentage } from '../../utils/formatting';
import { ReportStatus, ReportStatusLabels, ReportInterval } from '../../types/reports';
import { StatusBadge } from '../../components/ui/StatusBadge';
import type { StatusVariant } from '../../theme/colors';
import classes from '../../components/ui/reportTable.module.css';
import {
  GROUP_DIVIDER,
  GROUP_HEADER_TYPO,
  HEADER_GROUP_BG,
  HEADER_LABEL_BG,
  TOTALS_BG,
} from '../../components/ui/reportTable';

/**
 * Vier fixierte Spalten (Rang, Unternehmen, Werk, Vorjahres-Umschaltspalte) -- Referenzbreiten
 * aus dem Sketch-CSS (index.html:138-142). Die Umschaltspalte bleibt in diesem Plan strukturell
 * angelegt, aber leer (Interaktion folgt in 21-06/D-16) -- die Offset-Rechnung soll nicht
 * zweimal gebaut werden.
 */
const STICKY_WIDTHS = { rank: 44, company: 220, plant: 170, toggle: 40 } as const;
const STICKY_LEFT = {
  rank: 0,
  company: STICKY_WIDTHS.rank,
  plant: STICKY_WIDTHS.rank + STICKY_WIDTHS.company,
  toggle: STICKY_WIDTHS.rank + STICKY_WIDTHS.company + STICKY_WIDTHS.plant,
} as const;
const STICKY_TOTAL_WIDTH = STICKY_WIDTHS.rank + STICKY_WIDTHS.company + STICKY_WIDTHS.plant + STICKY_WIDTHS.toggle;

/**
 * Fix (Orchestrator-Befund, Pitfall 7): `table-layout: fixed` mit einer KONSTANTEN
 * min-width quetscht bei vielen aktiven Spaltengruppen alle Datenspalten auf denselben
 * Bruchteil zusammen (gemessen: 28px Zellbreite vs. 35-51px Inhaltsbreite bei 37 Spalten) --
 * Ueberlappung statt horizontalem Scroll. Die Mindestbreite der Tabelle muss deshalb mit der
 * tatsaechlichen Spaltenzahl (inkl. Δ-Vorjahr-Spalten) mitwachsen.
 */
/**
 * Auf das laengste Statusabzeichen bemessen: "In Bearbeitung" ist 101px breit, dazu 2x11px
 * Zellinnenabstand -- 130px lassen einen schmalen Rest, ohne dass rechts unnoetig Weissraum
 * stehen bleibt.
 */
const STATUS_COLUMN_WIDTH = 130;
const DATA_COLUMN_MIN_WIDTH = 90;
const DELTA_COLUMN_MIN_WIDTH = 70;

type ColumnGroupKey =
  | 'stamm'
  | 'stunden'
  | 'unfaelle'
  | 'ausfall'
  | 'jahresstd'
  | 'indirekt'
  | 'tote'
  | 'raten'
  | 'kontakt';

const GROUP_LABEL: Record<ColumnGroupKey, string> = {
  stamm: 'Stammdaten',
  stunden: 'Verfahrene Stunden',
  unfaelle: 'Unfälle ≥ 1 Ausfalltag',
  ausfall: 'Arbeitsausfalltage',
  jahresstd: 'Jahresarbeitsstunden',
  indirekt: 'Indirekt Beschäftigte',
  tote: 'Unfalltote & MTI',
  raten: 'Unfallhäufigkeitsraten',
  kontakt: 'Kontakt',
};

const GROUP_ORDER: ColumnGroupKey[] = [
  'stamm',
  'stunden',
  'unfaelle',
  'ausfall',
  'jahresstd',
  'indirekt',
  'tote',
  'raten',
  'kontakt',
];

/** D-14: Vorbelegung -- nur 'raten' ist beim ersten Rendern eingeschaltet. */
const DEFAULT_ENABLED_GROUPS: ColumnGroupKey[] = ['raten'];

interface ValueColumn {
  key: string;
  label: string;
  align?: 'left';
  strong?: boolean;
  render: (row: AccidentEvaluationRow) => number | string | null;
  total?: (totals: Partial<AccidentEvaluationValues>) => number | string | null;
  /**
   * D-17: nur die Kennzahlen- und Gesamtspalten bekommen eine Delta-Nachbarspalte
   * (Discretion-Entscheidung, nicht alle ~30 Werte). Vergleicht IMMER die aktuelle Zeile gegen
   * row.previousYear -- unabhaengig vom D-16-Tausch (aktuell-vs-Vorjahr, nie Vorjahr-vs-Vorjahr).
   */
  renderDelta?: (row: AccidentEvaluationRow) => number | null;
  /**
   * Zeigt unter dem Wert einen proportionalen Balken (Sketch-Fassung B). Nur fuer die
   * Gesamtrate sinnvoll: der Balken macht das Verhaeltnis der Werke zueinander sichtbar,
   * ohne dass man die Zahlenkolonne lesen muss.
   */
  bar?: boolean;
  /**
   * Eigene Spaltenbreite statt DATA_COLUMN_MIN_WIDTH. Nur setzen, wo der Inhalt es erzwingt --
   * einheitliche Breiten sind bei den Zahlenspalten der Regelfall (siehe columnWidths).
   */
  width?: number;
}

/**
 * Raten kommen als DECIMAL-String aus dem Backend. Fuer den Balken wird gerechnet, nicht
 * formatiert -- deshalb hier einmal sauber nach number, mit null bei fehlendem oder
 * unbrauchbarem Wert.
 */
function toRate(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Summe dreier moeglicherweise leerer Zaehlfelder; null nur wenn ALLE drei null sind. */
function sumNullable(...values: (number | null | undefined)[]): number | null {
  const present = values.filter((v): v is number => v !== null && v !== undefined);
  if (present.length === 0) return null;
  return present.reduce((acc, v) => acc + v, 0);
}

const GROUP_COLUMNS: Record<ColumnGroupKey, ValueColumn[]> = {
  stamm: [
    {
      key: 'clinker',
      label: 'Klinker',
      align: 'left',
      render: (r) => (r.clinkerProductionDuringPeriod === null ? null : r.clinkerProductionDuringPeriod ? 'ja' : 'nein'),
    },
    { key: 'employeesTotal', label: 'MA gesamt', render: (r) => r.employeesTotal, total: (t) => t.employeesTotal ?? null },
    { key: 'employeesAdmin', label: 'dav. Verw.', render: (r) => r.employeesAdmin, total: (t) => t.employeesAdmin ?? null },
  ],
  stunden: [
    { key: 'hoursWorkedWage', label: 'Lohn', render: (r) => r.hoursWorkedWage, total: (t) => t.hoursWorkedWage ?? null },
    { key: 'hoursWorkedAdmin', label: 'Verw.', render: (r) => r.hoursWorkedAdmin, total: (t) => t.hoursWorkedAdmin ?? null },
    { key: 'hoursWorkedTemp', label: 'Leih', render: (r) => r.hoursWorkedTemp, total: (t) => t.hoursWorkedTemp ?? null },
    {
      key: 'hoursWorkedTotal',
      label: 'Gesamt',
      strong: true,
      render: (r) => r.hoursWorkedTotal,
      total: (t) => t.hoursWorkedTotal ?? null,
      renderDelta: (r) => calculateDelta(r.hoursWorkedTotal, r.previousYear?.hoursWorkedTotal ?? null),
    },
  ],
  unfaelle: [
    {
      key: 'accidentsLostDayWage',
      label: 'Lohn',
      render: (r) => r.accidentsLostDayWage,
      total: (t) => t.accidentsLostDayWage ?? null,
    },
    {
      key: 'accidentsLostDayAdmin',
      label: 'Verw.',
      render: (r) => r.accidentsLostDayAdmin,
      total: (t) => t.accidentsLostDayAdmin ?? null,
    },
    {
      key: 'accidentsLostDayTemp',
      label: 'Leih',
      render: (r) => r.accidentsLostDayTemp,
      total: (t) => t.accidentsLostDayTemp ?? null,
    },
    {
      key: 'accidentsLostDayTotal',
      label: 'Gesamt',
      strong: true,
      render: (r) => r.accidentsLostDayTotal,
      total: (t) => t.accidentsLostDayTotal ?? null,
      renderDelta: (r) => calculateDelta(r.accidentsLostDayTotal, r.previousYear?.accidentsLostDayTotal ?? null),
    },
  ],
  ausfall: [
    { key: 'lostWorkDaysWage', label: 'Lohn', render: (r) => r.lostWorkDaysWage, total: (t) => t.lostWorkDaysWage ?? null },
    {
      key: 'lostWorkDaysAdmin',
      label: 'Verw.',
      render: (r) => r.lostWorkDaysAdmin,
      total: (t) => t.lostWorkDaysAdmin ?? null,
    },
    { key: 'lostWorkDaysTemp', label: 'Leih', render: (r) => r.lostWorkDaysTemp, total: (t) => t.lostWorkDaysTemp ?? null },
    {
      key: 'lostWorkDaysTotal',
      label: 'Gesamt',
      strong: true,
      render: (r) => r.lostWorkDaysTotal,
      total: (t) => t.lostWorkDaysTotal ?? null,
      renderDelta: (r) => calculateDelta(r.lostWorkDaysTotal, r.previousYear?.lostWorkDaysTotal ?? null),
    },
  ],
  /*
   * Quotientenfelder: die Fusszeile zeigt hier den GEPOOLTEN Wert (Gesamtstunden je
   * Gesamtkopfzahl), nicht die Summe der Werkswerte -- gerechnet im Backend, damit Zeile und
   * Fusszeile derselben Regel folgen.
   */
  jahresstd: [
    { key: 'annualHoursWage', label: 'Lohn', render: (r) => r.annualHoursWage, total: (t) => t.annualHoursWage ?? null },
    { key: 'annualHoursAdmin', label: 'Verw.', render: (r) => r.annualHoursAdmin, total: (t) => t.annualHoursAdmin ?? null },
    { key: 'annualHoursTemp', label: 'Leih', render: (r) => r.annualHoursTemp, total: (t) => t.annualHoursTemp ?? null },
    {
      key: 'annualHoursTotal',
      label: 'Gesamt',
      strong: true,
      render: (r) => r.annualHoursTotal,
      total: (t) => t.annualHoursTotal ?? null,
    },
    // Plan 25.1-04: fuenfte und letzte Spalte der Gruppe, dieselbe Position wie im XLSX-Export
    // (Plan 25.1-03, dort Label "Jahresarbeitsstunden ohne KANUE"). Keine Delta-Nachbarspalte --
    // keine der vier bestehenden Spalten dieser Gruppe hat eine, `jahresstd` ist die eine bewusste
    // Ausnahme im gesamten Tabellensatz. KEIN `strong` -- das traegt in dieser Gruppe allein
    // annualHoursTotal als Gesamtspalte, das neue Feld ist keine Summe, sondern ein eigener
    // gepoolter Durchschnitt.
    {
      key: 'annualHoursCombined',
      label: 'Ø ohne KANÜ',
      render: (r) => r.annualHoursCombined,
      total: (t) => t.annualHoursCombined ?? null,
    },
  ],
  indirekt: [
    {
      key: 'indirectHoursWorked',
      label: 'Std.',
      render: (r) => r.indirectHoursWorked,
      total: (t) => t.indirectHoursWorked ?? null,
    },
    { key: 'indirectLtis', label: 'LTI', render: (r) => r.indirectLtis, total: (t) => t.indirectLtis ?? null },
  ],
  tote: [
    {
      key: 'fatalitiesOperational',
      label: 'Betrieb',
      render: (r) => sumNullable(r.fatalitiesWage, r.fatalitiesAdmin, r.fatalitiesTemp),
      total: (t) => sumNullable(t.fatalitiesWage, t.fatalitiesAdmin, t.fatalitiesTemp),
    },
    {
      key: 'fatalitiesCommuting',
      label: 'Wege',
      render: (r) => r.fatalitiesCommuting,
      total: (t) => t.fatalitiesCommuting ?? null,
    },
    { key: 'mtiOwnEmployees', label: 'MTI', render: (r) => r.mtiOwnEmployees, total: (t) => t.mtiOwnEmployees ?? null },
    {
      key: 'indirectFatalities',
      label: 'ind. Tote',
      render: (r) => r.indirectFatalities,
      total: (t) => t.indirectFatalities ?? null,
    },
    {
      key: 'mtiThirdParties',
      label: 'MTI Dritte',
      render: (r) => r.mtiThirdParties,
      total: (t) => t.mtiThirdParties ?? null,
    },
  ],
  raten: [
    {
      key: 'accidentRateOwn',
      label: 'eigene',
      render: (r) => r.accidentRateOwn,
      total: (t) => t.accidentRateOwn ?? null,
      renderDelta: (r) => calculateDelta(r.accidentRateOwn, r.previousYear?.accidentRateOwn ?? null),
    },
    {
      key: 'accidentRateIndirect',
      label: 'indirekte',
      render: (r) => r.accidentRateIndirect,
      total: (t) => t.accidentRateIndirect ?? null,
      renderDelta: (r) => calculateDelta(r.accidentRateIndirect, r.previousYear?.accidentRateIndirect ?? null),
    },
    {
      key: 'accidentRateTotal',
      label: 'gesamt',
      strong: true,
      bar: true,
      render: (r) => r.accidentRateTotal,
      total: (t) => t.accidentRateTotal ?? null,
      renderDelta: (r) => calculateDelta(r.accidentRateTotal, r.previousYear?.accidentRateTotal ?? null),
    },
  ],
  /*
   * Kontaktspalten tragen Freitext, keine Zahlen -- bei der Regelbreite von 90px wurden alle drei
   * abgeschnitten (gemessen: E-Mail braucht 221px, Ansprechpartner 97px, Telefon 88px). Eigene
   * Breiten statt Ellipse, weil eine halbe E-Mail-Adresse niemandem nuetzt.
   */
  kontakt: [
    { key: 'contactName', label: 'Ansprechpartner', align: 'left', width: 150, render: (r) => r.contactName },
    { key: 'contactPhone', label: 'Telefon', align: 'left', width: 120, render: (r) => r.contactPhone },
    { key: 'contactEmail', label: 'E-Mail', align: 'left', width: 250, render: (r) => r.contactEmail },
  ],
};

/** 'pending' fasst Entwurf UND fehlende Meldung zusammen (D-05) -- kein eigener ReportStatus-Wert. */
const ROW_STATUS_LABELS: Record<AccidentEvaluationRowStatus, string> = {
  [ReportStatus.IN_PROGRESS]: ReportStatusLabels[ReportStatus.IN_PROGRESS],
  [ReportStatus.SUBMITTED]: ReportStatusLabels[ReportStatus.SUBMITTED],
  [ReportStatus.APPROVED]: ReportStatusLabels[ReportStatus.APPROVED],
  [ReportStatus.REVISION_NEEDED]: ReportStatusLabels[ReportStatus.REVISION_NEEDED],
  pending: 'Ausstehend',
};

/**
 * Statusfarben kommen aus der geteilten `statusPalette` (theme/colors.ts, Figma-verifiziert) und
 * werden ueber `StatusBadge` gerendert -- dieselbe Farbwelt wie in der Meldungsliste. 'pending'
 * hat keine eigene Variante: ein Werk ohne Meldung ist ein Fehlstand, deshalb die fehler-Farbwelt
 * mit domaenenspezifischer Beschriftung.
 */
const ROW_STATUS_VARIANTS: Record<AccidentEvaluationRowStatus, StatusVariant> = {
  [ReportStatus.APPROVED]: 'freigegeben',
  [ReportStatus.SUBMITTED]: 'eingereicht',
  [ReportStatus.IN_PROGRESS]: 'inBearbeitung',
  [ReportStatus.REVISION_NEEDED]: 'revision',
  pending: 'fehler',
};

type StatusFilterKey = 'alle' | 'approved' | 'submitted' | 'inBearbeitung' | 'pending';

function matchesStatusFilter(row: AccidentEvaluationRow, filter: StatusFilterKey): boolean {
  if (filter === 'alle') return true;
  if (filter === 'inBearbeitung') {
    return row.status === ReportStatus.IN_PROGRESS || row.status === ReportStatus.REVISION_NEEDED;
  }
  if (filter === 'approved') return row.status === ReportStatus.APPROVED;
  if (filter === 'submitted') return row.status === ReportStatus.SUBMITTED;
  return row.status === 'pending';
}

/**
 * Cockpit nach Sketch 001 Variante B (D-03-eigener Einstiegspunkt, kein Breadcrumb zum
 * Auswertungs-Hub /evaluations). Jahr kommt serverseitig vorbelegt (D-06): ohne 'year' in der
 * URL liefert der Endpoint bereits das neueste Meldejahr, availableYears kommt IMMER aus der
 * Antwort -- kein clientseitig berechneter Kalenderjahr-Bereich.
 *
 * Vorjahres-Umschaltspalte (D-16): Hover/Klick tauscht die Wertzellen einer Zeile gegen
 * row.previousYear. Delta-Spalten (D-17) sind ueber den 'Δ Vorjahr'-Chip zuschaltbar. Export
 * laeuft ueber dieselbe Service-Klasse wie die Bildschirmdaten (D-11) -- immer vollstaendig.
 */
export function AccidentEvaluationPage() {
  const [searchParams] = useSearchParams();
  const yearParam = searchParams.get('year');
  const requestedYear = yearParam ? Number(yearParam) : undefined;

  const [data, setData] = useState<AccidentEvaluationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await accidentEvaluationApi.getAll(requestedYear);
        if (!cancelled) setData(response);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Daten konnten nicht geladen werden.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [requestedYear]);

  const status = data?.reportingStatus ?? null;
  const kpis = data?.industryKpis ?? null;

  const rateDelta = kpis ? calculateDelta(kpis.accidentRateTotal, kpis.accidentRateTotalPrevYear) : null;
  const inProgressCount = status ? status.inProgress + status.revisionNeeded : 0;

  const [statusFilter, setStatusFilter] = useState<StatusFilterKey>('alle');
  const [enabledGroups, setEnabledGroups] = useState<ColumnGroupKey[]>(DEFAULT_ENABLED_GROUPS);

  /**
   * D-16: Vorjahres-Werte-Tausch je Zeile. Gepinnt (Klick, bleibt bis erneuter Klick) UND
   * gehovert (fluechtige Vorschau) fuehren beide zum Tausch -- eine Zeile gilt als getauscht,
   * wenn mindestens eine der beiden Bedingungen zutrifft.
   */
  const [pinnedPlantIds, setPinnedPlantIds] = useState<Set<number>>(new Set());
  const [hoveredPlantId, setHoveredPlantId] = useState<number | null>(null);

  const togglePinnedPlant = useCallback((plantId: number) => {
    setPinnedPlantIds((prev) => {
      const next = new Set(prev);
      if (next.has(plantId)) {
        next.delete(plantId);
      } else {
        next.add(plantId);
      }
      return next;
    });
  }, []);

  /** D-17: gleiche Chip-Mechanik wie die Spaltengruppen -- fluechtiger Zustand, nicht gespeichert. */
  const [showDeltaColumns, setShowDeltaColumns] = useState(false);

  /**
   * Export ist immer vollstaendig (einziger Parameter: das Berichtsjahr) -- haengt bewusst nicht
   * am Statusfilter- oder Gruppen-State.
   */
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async () => {
    if (!data) return;
    setExporting(true);
    setExportError(null);
    try {
      const blob = await accidentEvaluationApi.export(data.year);
      downloadBlob(blob, `Unfallstatistik_${data.year}.xlsx`);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setExporting(false);
    }
  }, [data]);

  const rows = data?.rows ?? [];
  const visibleRows = rows.filter((row) => matchesStatusFilter(row, statusFilter));
  const visibleGroups = GROUP_ORDER.filter((g) => enabledGroups.includes(g));

  /**
   * Bezugsgroesse fuer den Ratenbalken: die hoechste Rate der SICHTBAREN Zeilen. Der Sketch
   * skalierte gegen eine feste 22 -- ein Platzhalterwert, der bei realen Daten entweder
   * abschneidet oder alle Balken winzig macht. Gegen das tatsaechliche Maximum skaliert bleibt
   * der Vergleich untereinander in jedem Datenstand lesbar.
   */
  const maxVisibleRate = visibleRows.reduce<number>((acc, row) => {
    const rate = toRate(row.accidentRateTotal);
    return rate !== null && rate > acc ? rate : acc;
  }, 0);

  /**
   * Spaltenbreiten in Renderreihenfolge -- einzige Quelle fuer das colgroup UND fuer die
   * Mindestbreite der Tabelle (Pitfall 7: bei `table-layout: fixed` und zu kleiner Mindestbreite
   * quetscht der Browser die Spalten zusammen, statt die ScrollArea scrollen zu lassen).
   *
   * Einheitliche Breite fuer Zahlenspalten ist Absicht, nicht Bequemlichkeit: rechtsbuendige
   * Zahlen in gleich breiten Spalten bilden eine saubere Lesekante, und inhaltsabhaengige
   * Breiten wuerden bei jedem Zu-/Abschalten einer Gruppe die fixierten Spalten verschieben.
   * `col.width` weicht nur dort ab, wo der Inhalt es erzwingt (Kontaktspalten).
   */
  const columnWidths: number[] = [
    STICKY_WIDTHS.rank,
    STICKY_WIDTHS.company,
    STICKY_WIDTHS.plant,
    STICKY_WIDTHS.toggle,
    STATUS_COLUMN_WIDTH,
    ...visibleGroups.flatMap((g) =>
      GROUP_COLUMNS[g].flatMap((col) =>
        showDeltaColumns && col.renderDelta
          ? [col.width ?? DATA_COLUMN_MIN_WIDTH, DELTA_COLUMN_MIN_WIDTH]
          : [col.width ?? DATA_COLUMN_MIN_WIDTH]
      )
    ),
  ];
  const tableMinWidth = columnWidths.reduce((acc, w) => acc + w, 0);

  const stickyCellStyle = (
    left: number,
    background: string | undefined,
    extra?: React.CSSProperties
  ): React.CSSProperties => ({
    position: 'sticky',
    left,
    zIndex: 1,
    backgroundColor: background ?? 'var(--mantine-color-body)',
    ...extra,
  });

  const stickyHeaderStyle = (
    left: number,
    width: number,
    background: string,
    extra?: React.CSSProperties
  ): React.CSSProperties => ({
    position: 'sticky',
    left,
    zIndex: 2,
    backgroundColor: background,
    width,
    ...extra,
  });

  /**
   * Ratenbalken unter der Gesamtrate (Sketch-Fassung B). Breite proportional zur hoechsten
   * sichtbaren Rate.
   *
   * ABWEICHUNG VOM SKETCH, BEWUSST: der Sketch faerbt jede Abweichung von mehr als 50 % zur
   * Branchenrate ein -- auch die nach UNTEN. Bei Unfallzahlen ist deutlich unter dem
   * Branchenschnitt aber das gewuenschte Ergebnis; eine Warnfarbe dafuer waere ein falsches
   * Signal. Eingefaerbt wird deshalb nur, wer deutlich darueber liegt -- dieselbe Polaritaet
   * wie bei den Delta-Spalten (getDeltaColor mit positiveIsGood=false).
   */
  const renderRateBar = (rate: number) => {
    const reference = maxVisibleRate > 0 ? maxVisibleRate : rate;
    const percent = reference > 0 ? Math.min(100, (rate / reference) * 100) : 0;
    const branchRate = toRate(kpis?.accidentRateTotal);
    const wellAboveBranch = branchRate !== null && branchRate > 0 && (rate - branchRate) / branchRate > 0.5;

    return (
      <Box
        aria-hidden
        style={{
          position: 'absolute',
          left: 10,
          right: 10,
          bottom: 6,
          height: 3,
          borderRadius: 2,
          backgroundColor: 'var(--mantine-color-vdzGray-2)',
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: `${percent}%`,
            height: '100%',
            borderRadius: 2,
            backgroundColor: wellAboveBranch
              ? 'var(--mantine-color-vdzOrange-6)'
              : 'var(--mantine-color-vdzGray-6)',
          }}
        />
      </Box>
    );
  };

  /**
   * Eine Werkszeile der Tabelle. Fester Name (Pflicht) -- 21-05/Task2 und 21-06/Task2 extrahieren
   * diesen Block per awk und pruefen ihn auf Einzeiligkeit (D-17-Vorbereitung). KEIN Client-Sort:
   * die Reihenfolge kommt unveraendert aus data.rows (D-07/D-08/D-09).
   */
  const renderPlantRow = (row: AccidentEvaluationRow) => {
    const isPending = row.status === 'pending';
    const isSwapped = (pinnedPlantIds.has(row.plantId) || hoveredPlantId === row.plantId) && row.previousYear !== null;
    const previousYearLabel = data ? data.year - 1 : null;
    /**
     * D-16: nur die Wertzellen (visibleGroups) lesen bei aktivem Tausch aus row.previousYear --
     * Rang, Unternehmen, Werk und Status bleiben die der aktuellen Zeile (der Rang gehoert zum
     * aktuellen Jahr, nicht zum Vorjahr).
     */
    const valuesRow: AccidentEvaluationRow = isSwapped && row.previousYear ? { ...row, ...row.previousYear } : row;
    /*
     * Grund- und Hoverfarbe der Zeile werden hier entschieden und als Custom Properties gesetzt;
     * den Tausch beim Hover erledigt die CSS-Regel im Modul (siehe dort, warum nicht State und
     * nicht Mantines highlightOnHover). Die fixierten Zellen lesen dieselbe Variable und ziehen
     * dadurch mit.
     *
     * Eine gepinnte Vorjahreszeile behaelt ihr Orange auch beim Hover -- der Pin ist ein
     * gehaltener Zustand, den ein Mauszeiger nicht ueberdecken soll.
     */
    const rowTint = isSwapped
      ? 'var(--mantine-color-orange-1)'
      : isPending
        ? '#fffafa'
        : 'var(--mantine-color-body)';
    const rowTintHover = isSwapped
      ? 'var(--mantine-color-orange-1)'
      : isPending
        ? '#fff5f5'
        : // vdzGray-1 statt -0: der hellste Ton unterscheidet sich mit 2% kaum von Weiss und
          // waere als Highlight nicht wahrnehmbar. Mit der Kopfzeile (ebenfalls vdzGray-1) ist
          // eine Verwechslung nicht zu befuerchten -- die steht oben und traegt das dunklere
          // Gruppenband ueber sich.
          'var(--mantine-color-vdzGray-1)';

    return (
      <Table.Tr
        key={row.plantId}
        className={classes.dataRow}
        style={
          {
            '--row-bg-base': rowTint,
            '--row-bg-hover': rowTintHover,
            backgroundColor: 'var(--row-bg)',
          } as React.CSSProperties
        }
      >
        <Table.Td style={stickyCellStyle(STICKY_LEFT.rank, 'var(--row-bg)', { width: STICKY_WIDTHS.rank })}>
          <Text c="vdzGray.6" fz="sm">{row.rank ?? ''}</Text>
        </Table.Td>
        <Table.Td
          style={stickyCellStyle(STICKY_LEFT.company, 'var(--row-bg)', {
            width: STICKY_WIDTHS.company,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          <Text fz="sm">{row.companyName}</Text>
        </Table.Td>
        <Table.Td
          style={stickyCellStyle(STICKY_LEFT.plant, 'var(--row-bg)', {
            width: STICKY_WIDTHS.plant,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            boxShadow: '2px 0 0 var(--mantine-color-gray-3)',
          })}
        >
          <Group gap={4} wrap="nowrap" style={{ overflow: 'hidden' }}>
            <Text fz="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.plantName}
            </Text>
            {isSwapped && (
              <Badge size="xs" color="orange" variant="filled" style={{ flexShrink: 0 }}>
                {previousYearLabel}
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td
          style={stickyCellStyle(STICKY_LEFT.toggle, 'var(--row-bg)', { width: STICKY_WIDTHS.toggle, textAlign: 'center' })}
        >
          {row.previousYear ? (
            <ActionIcon
              variant={pinnedPlantIds.has(row.plantId) ? 'filled' : 'subtle'}
              color="vdzGray"
              size="sm"
              aria-label={`Vorjahresvergleich ${row.plantName}`}
              onMouseEnter={() => setHoveredPlantId(row.plantId)}
              onMouseLeave={() => setHoveredPlantId((prev) => (prev === row.plantId ? null : prev))}
              onClick={() => togglePinnedPlant(row.plantId)}
            >
              <IconHistory size={16} />
            </ActionIcon>
          ) : (
            <Tooltip label={`Keine Meldung für ${previousYearLabel ?? ''}`}>
              <Box>
                <ActionIcon variant="subtle" color="vdzGray" size="sm" disabled aria-label="Kein Vorjahreswert">
                  <IconHistory size={16} />
                </ActionIcon>
              </Box>
            </Tooltip>
          )}
        </Table.Td>
        <Table.Td style={{ borderLeft: GROUP_DIVIDER }}>
          <StatusBadge status={ROW_STATUS_VARIANTS[row.status]} label={ROW_STATUS_LABELS[row.status]} />
        </Table.Td>
        {visibleGroups.flatMap((g) =>
          GROUP_COLUMNS[g].flatMap((col, colIndex) => {
            const value = col.render(valuesRow);
            const valueCell = (
              <Table.Td
                key={col.key}
                style={{
                  textAlign: col.align === 'left' ? 'left' : 'right',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  ...(colIndex === 0 ? { borderLeft: GROUP_DIVIDER } : {}),
                  ...(col.bar ? { position: 'relative', paddingBottom: 14 } : {}),
                }}
              >
                {value === null ? (
                  <Text fz="sm" c="vdzGray.5">–</Text>
                ) : (
                  <Text fz="sm" fw={col.strong ? 700 : 400}>
                    {typeof value === 'string' && col.align === 'left' ? value : formatNumber(value, col.key === 'accidentRateOwn' || col.key === 'accidentRateIndirect' || col.key === 'accidentRateTotal' ? 2 : 0)}
                  </Text>
                )}
                {col.bar ? (() => { const rate = toRate(value); return rate === null ? null : renderRateBar(rate); })() : null}
              </Table.Td>
            );
            /*
             * D-17: Delta vergleicht IMMER die aktuelle Zeile (row, nicht valuesRow) gegen
             * row.previousYear -- bei per D-16 getauschter Zeile bleibt das Delta
             * aktuell-vs-Vorjahr, nicht Vorjahr-vs-Vorjahr.
             */
            if (showDeltaColumns && col.renderDelta) {
              const delta = col.renderDelta(row);
              return [
                valueCell,
                <Table.Td key={`${col.key}-delta`} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Text fz="xs" fw={500} style={{ color: getDeltaColor(delta, false) }}>
                    {formatPercentage(delta)}
                  </Text>
                </Table.Td>,
              ];
            }
            return [valueCell];
          })
        )}
        <Table.Td style={{ borderLeft: GROUP_DIVIDER }} />
      </Table.Tr>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={data ? `Auswertung Unfalldaten ${data.year}` : 'Auswertung Unfalldaten'}
        rightSection={
          <Group gap="md" align="flex-end">
            {/* D-01/D-06: die Unfallmeldung ist jahresbasiert, weil die Unfallmeldung es ist --
                kein EvaluationMapping-Eintrag, aus dem das Intervall bezogen werden koennte. Der
                Rechenkern ist geteilt (PeriodFilter), die Periodizitaet bleibt hier Sache des
                Aufrufers (D-01). Datenweg unveraendert: availableYears kommt weiterhin aus
                AccidentEvaluationService::build() -- der Endpunkt aus 22-08 deckt nur die fuenf
                Marktauswertungen ab und wird hier bewusst nicht angebunden. */}
            <PeriodFilter
              interval={ReportInterval.YEARLY}
              availableYears={data?.availableYears ?? []}
              loading={loading}
            />
            <Button
              variant="default"
              color="vdzGray"
              leftSection={<IconDownload size={16} />}
              loading={exporting}
              disabled={!data}
              onClick={handleExport}
            >
              Export (XLSX)
            </Button>
            <Tooltip label="Funktion folgt später" position="bottom">
              <Box>
                <Button disabled variant="default" color="vdzGray">
                  Auswertung finalisieren
                </Button>
              </Box>
            </Tooltip>
          </Group>
        }
      />

      {error && (
        <Alert color="red" title="Fehler">
          {error}
        </Alert>
      )}

      {exportError && (
        <Alert color="red" title="Export fehlgeschlagen" withCloseButton onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={6} />
      ) : !data || data.availableYears.length === 0 ? (
        <Alert color="vdzGray" title="Keine Daten">
          Noch keine Unfalldaten-Meldungen im System.
        </Alert>
      ) : (
        status && kpis && (
          <>
            <Card padding="md" radius="md" withBorder>
              <Stack gap="sm">
                <Progress.Root size="lg">
                  <Progress.Section
                    value={status.totalPlants > 0 ? (status.approved / status.totalPlants) * 100 : 0}
                    color="green"
                  />
                  <Progress.Section
                    value={status.totalPlants > 0 ? (status.submitted / status.totalPlants) * 100 : 0}
                    color="yellow"
                  />
                  <Progress.Section
                    value={status.totalPlants > 0 ? (inProgressCount / status.totalPlants) * 100 : 0}
                    color="grape"
                  />
                </Progress.Root>

                <Group gap="lg">
                  <Text size="sm">
                    <Text span fw={700}>
                      {status.approved}
                    </Text>{' '}
                    genehmigt
                  </Text>
                  <Text size="sm">
                    <Text span fw={700}>
                      {status.submitted}
                    </Text>{' '}
                    eingereicht
                  </Text>
                  <Text size="sm">
                    <Text span fw={700}>
                      {inProgressCount}
                    </Text>{' '}
                    in Bearbeitung
                  </Text>
                  <Text size="sm">
                    <Text span fw={700}>
                      {status.pending}
                    </Text>{' '}
                    ausstehend
                  </Text>
                  <Text size="sm" c="vdzGray.6">
                    von {status.totalPlants} Werken
                  </Text>
                </Group>

                {status.pending > 0 ? (
                  <Text size="sm">
                    <Text span fw={700}>
                      Noch nicht gemeldet:
                    </Text>{' '}
                    {status.pendingPlants.map((plant, idx) => (
                      <Text span key={`${plant.plantName}-${idx}`}>
                        {idx > 0 && ' · '}
                        {plant.plantName} <Text span c="vdzGray.6">({plant.companyName})</Text>
                      </Text>
                    ))}
                  </Text>
                ) : (
                  <Text size="sm" c="green.7" fw={700}>
                    Alle Werke haben gemeldet.
                  </Text>
                )}

                {data && (
                  <TransferLinkRow
                    evaluationType={UNFALLDATEN_TYPE}
                    year={data.year}
                    period={1}
                  />
                )}
              </Stack>
            </Card>

            <Title order={2} fz="h4" fw={700} mb={-4}>
              Branchenkennzahlen
            </Title>

            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
              <Card padding="md" radius="md" withBorder>
                <Stack gap={4}>
                  <Text size="xs" c="vdzGray.6" tt="uppercase" fw={600}>
                    Unfallhäufigkeitsrate gesamt
                  </Text>
                  <Text size="xl" fw={700}>
                    {formatNumber(kpis.accidentRateTotal, 2)}
                  </Text>
                  {kpis.accidentRateTotalPrevYear === null ? (
                    <Text size="xs" fs="italic" c="vdzGray.6">
                      kein Vorjahreswert im System
                    </Text>
                  ) : (
                    <Text size="xs" fw={600} style={{ color: getDeltaColor(rateDelta, false) }}>
                      {formatPercentage(rateDelta)} ggü. Vorjahr
                    </Text>
                  )}
                </Stack>
              </Card>

              <Card padding="md" radius="md" withBorder>
                <Stack gap={4}>
                  <Text size="xs" c="vdzGray.6" tt="uppercase" fw={600}>
                    Unfälle ≥ 1 Ausfalltag
                  </Text>
                  <Text size="xl" fw={700}>
                    {formatNumber(kpis.accidentsLostDay, 0)}
                  </Text>
                  <Text size="xs" c="vdzGray.6">
                    + {formatNumber(kpis.accidentsLostDayIndirect, 0)} bei indirekt Beschäftigten
                  </Text>
                </Stack>
              </Card>

              <Card padding="md" radius="md" withBorder>
                <Stack gap={4}>
                  <Text size="xs" c="vdzGray.6" tt="uppercase" fw={600}>
                    Arbeitsausfalltage
                  </Text>
                  <Text size="xl" fw={700}>
                    {formatNumber(kpis.lostWorkDays, 0)}
                  </Text>
                  {kpis.lostWorkDaysPerAccident !== null && (
                    <Text size="xs" c="vdzGray.6">
                      ⌀ {formatNumber(kpis.lostWorkDaysPerAccident, 1)} Tage je Unfall
                    </Text>
                  )}
                </Stack>
              </Card>

              <Card padding="md" radius="md" withBorder>
                <Stack gap={4}>
                  <Text size="xs" c="vdzGray.6" tt="uppercase" fw={600}>
                    Unfalltote
                  </Text>
                  <Text size="xl" fw={700} c={kpis.fatalities !== null && kpis.fatalities > 0 ? 'red.7' : undefined}>
                    {formatNumber(kpis.fatalities, 0)}
                  </Text>
                  <Text size="xs" c="vdzGray.6">
                    inkl. Wegeunfälle · + {formatNumber(kpis.fatalitiesIndirect, 0)} bei indirekt Beschäftigten ·{' '}
                    {formatNumber(kpis.mti, 0)} MTI
                  </Text>
                </Stack>
              </Card>
            </SimpleGrid>

            <Text size="sm" c="vdzGray.7">
              Berechnet über{' '}
              <Text span fw={700} c="vdzGray.9">
                {status.kpiBasis} von {status.totalPlants}
              </Text>{' '}
              Werken — eingereichte und genehmigte Meldungen.
              {inProgressCount > 0 && (
                <>
                  {' '}
                  {inProgressCount === 1 ? 'Eine Meldung ist' : `${inProgressCount} Meldungen sind`} noch in
                  Bearbeitung und deshalb unvollständig;{' '}
                  {inProgressCount === 1 ? 'sie fließt' : 'sie fließen'} hier nicht ein,{' '}
                  {inProgressCount === 1 ? 'steht' : 'stehen'} aber in der Werksliste unten.
                </>
              )}
              {status.pending > 0 && (
                <> {status.pending === 1 ? '1 Werk hat' : `${status.pending} Werke haben`} noch nicht gemeldet.</>
              )}
            </Text>

            <Title order={2} fz="h4" fw={700} mt="md" mb={-4}>
              Werksrangliste
            </Title>

            <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
              <Chip.Group value={statusFilter} onChange={(v) => setStatusFilter(v as StatusFilterKey)}>
                <Group gap="xs">
                  <Chip value="alle">Alle ({status.totalPlants})</Chip>
                  <Chip value="approved">Genehmigt ({status.approved})</Chip>
                  <Chip value="submitted">Eingereicht ({status.submitted})</Chip>
                  <Chip value="inBearbeitung">In Bearbeitung ({inProgressCount})</Chip>
                  <Chip value="pending">Ausstehend ({status.pending})</Chip>
                </Group>
              </Chip.Group>

              <Group gap="xs" wrap="wrap">
                <Chip.Group multiple value={enabledGroups} onChange={(v) => setEnabledGroups(v as ColumnGroupKey[])}>
                  <Group gap="xs">
                    {GROUP_ORDER.map((g) => (
                      <Chip key={g} value={g}>
                        {GROUP_LABEL[g]}
                      </Chip>
                    ))}
                  </Group>
                </Chip.Group>
                <Chip checked={showDeltaColumns} onChange={() => setShowDeltaColumns((prev) => !prev)}>
                  Δ Vorjahr
                </Chip>
              </Group>
            </Group>

            {/*
              * Ohne Innenabstand: die Tabelle bringt ihre eigene Randstruktur mit (getoentes
              * Kopfband, gelbe Gesamtzeile, Gruppengrenzen). Ein Card-Padding liesse davor einen
              * weissen Rahmen stehen, in dem diese Flaechen mitten in der Karte enden statt an
              * ihrer Kante. `overflow: hidden` haelt die Tabellenecken innerhalb des Eckradius.
              *
              * paddingTop/Bottom muessen explizit ueberschrieben werden: das Theme setzt sie
              * global fuer alle Cards ueber `styles.root` (theme.ts), und das schlaegt das
              * padding-Prop. Nur diese eine Karte weicht ab, das Theme bleibt unangetastet.
              */}
            <Card
              padding={0}
              radius="md"
              withBorder
              style={{ overflow: 'hidden', paddingTop: 0, paddingBottom: 0 }}
            >
              <ScrollArea type="auto">
                <Table style={{ minWidth: tableMinWidth, tableLayout: 'fixed' }}>
                  {/*
                    * Spaltenbreiten gehoeren bei `table-layout: fixed` ins colgroup, nicht in die
                    * Kopfzellen: massgeblich waere sonst die ERSTE Kopfzeile, und die besteht seit
                    * dem zweizeiligen Kopf aus colSpan-Zellen -- die fixierten Spalten verloeren
                    * dadurch ihre Breite und die sticky-left-Offsets wuerden nicht mehr passen.
                    * Die letzte Spalte bleibt absichtlich ohne Breite: sie schluckt ueberschuessigen
                    * Platz, damit er NICHT anteilig auf die fixierten Spalten verteilt wird.
                    */}
                  <colgroup>
                    {columnWidths.map((width, index) => (
                      <col key={index} style={{ width }} />
                    ))}
                    {/* Fuellspalte ohne Breite: sie schluckt den Ueberschuss, wenn wenige
                        Gruppen aktiv sind. Ohne sie wuerde eine echte Spalte auf ein
                        Vielfaches ihrer Breite aufgeblasen -- bei der Gesamtrate zoege sich
                        dann auch der Ratenbalken ueber die halbe Tabelle. */}
                    <col />
                  </colgroup>
                  <Table.Thead>
                    {/*
                      * Gruppenzeile. Die vier fixierten Spalten bilden EINE fixierte Gruppenzelle
                      * (wie im Sketch) -- ein rowSpan ueber beide Kopfzeilen wuerde die
                      * Beschriftungen stattdessen mittig zwischen den Zeilen aufhaengen.
                      */}
                    <Table.Tr style={{ backgroundColor: HEADER_GROUP_BG }}>
                      <Table.Th
                        colSpan={4}
                        style={stickyHeaderStyle(0, STICKY_TOTAL_WIDTH, HEADER_GROUP_BG, {
                          textAlign: 'left',
                          ...GROUP_HEADER_TYPO,
                        })}
                      >
                        Werk
                      </Table.Th>
                      <Table.Th
                        ta="center"
                        style={{
                          backgroundColor: HEADER_GROUP_BG,
                          borderLeft: GROUP_DIVIDER,
                          ...GROUP_HEADER_TYPO,
                        }}
                      >
                        Meldung
                      </Table.Th>
                      {visibleGroups.map((g) => (
                        <Table.Th
                          key={g}
                          colSpan={GROUP_COLUMNS[g].reduce(
                            (acc, col) => acc + (showDeltaColumns && col.renderDelta ? 2 : 1),
                            0
                          )}
                          ta="center"
                          style={{
                            backgroundColor: HEADER_GROUP_BG,
                            borderLeft: GROUP_DIVIDER,
                            ...GROUP_HEADER_TYPO,
                          }}
                        >
                          {GROUP_LABEL[g]}
                        </Table.Th>
                      ))}
                      <Table.Th style={{ backgroundColor: HEADER_GROUP_BG, borderLeft: GROUP_DIVIDER }} />
                    </Table.Tr>
                    <Table.Tr style={{ backgroundColor: HEADER_LABEL_BG }}>
                      <Table.Th style={stickyHeaderStyle(STICKY_LEFT.rank, STICKY_WIDTHS.rank, HEADER_LABEL_BG)}>
                        Rang
                      </Table.Th>
                      <Table.Th
                        style={stickyHeaderStyle(STICKY_LEFT.company, STICKY_WIDTHS.company, HEADER_LABEL_BG)}
                      >
                        Unternehmen
                      </Table.Th>
                      <Table.Th style={stickyHeaderStyle(STICKY_LEFT.plant, STICKY_WIDTHS.plant, HEADER_LABEL_BG)}>
                        Werk
                      </Table.Th>
                      <Table.Th
                        style={stickyHeaderStyle(STICKY_LEFT.toggle, STICKY_WIDTHS.toggle, HEADER_LABEL_BG)}
                        aria-label="Vorjahresvergleich"
                      />
                      <Table.Th style={{ backgroundColor: HEADER_LABEL_BG, borderLeft: GROUP_DIVIDER }}>
                        Status
                      </Table.Th>
                      {visibleGroups.flatMap((g) =>
                        GROUP_COLUMNS[g].flatMap((col, colIndex) => {
                          const labelTh = (
                            <Table.Th
                              key={col.key}
                              style={{
                                textAlign: col.align === 'left' ? 'left' : 'right',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                backgroundColor: HEADER_LABEL_BG,
                                ...(colIndex === 0 ? { borderLeft: GROUP_DIVIDER } : {}),
                              }}
                            >
                              {col.label}
                            </Table.Th>
                          );
                          if (showDeltaColumns && col.renderDelta) {
                            return [
                              labelTh,
                              <Table.Th
                                key={`${col.key}-delta`}
                                style={{
                                  textAlign: 'right',
                                  fontSize: '0.7rem',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  backgroundColor: HEADER_LABEL_BG,
                                }}
                              >
                                Δ VJ
                              </Table.Th>,
                            ];
                          }
                          return [labelTh];
                        })
                      )}
                      <Table.Th style={{ backgroundColor: HEADER_LABEL_BG, borderLeft: GROUP_DIVIDER }} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{visibleRows.map((row) => renderPlantRow(row))}</Table.Tbody>
                  <Table.Tfoot>
                    <Table.Tr style={{ backgroundColor: TOTALS_BG, fontWeight: 700 }}>
                      {/* Die fixierten Zellen muessen das Gelb der Zeile selbst tragen -- sonst
                          scrollen die Zahlen unter weissen Kaesten durch. */}
                      <Table.Td style={stickyCellStyle(STICKY_LEFT.rank, TOTALS_BG)} />
                      {/*
                        * "Gesamt", nicht "Summe": die Zeile enthaelt Mengen ALS Summe, aber
                        * Verhaeltniszahlen als gepoolten Wert. Die Werksanzahl daneben macht
                        * ausserdem sichtbar, dass hier alle angezeigten Werke zaehlen -- die
                        * Branchenkennzahlen oben rechnen bewusst ueber die engere Basis.
                        */}
                      <Table.Td style={stickyCellStyle(STICKY_LEFT.company, TOTALS_BG)}>Gesamt</Table.Td>
                      <Table.Td style={stickyCellStyle(STICKY_LEFT.plant, TOTALS_BG)}>
                        {visibleRows.length} {visibleRows.length === 1 ? 'Werk' : 'Werke'}
                      </Table.Td>
                      <Table.Td style={stickyCellStyle(STICKY_LEFT.toggle, TOTALS_BG)} />
                      <Table.Td style={{ borderLeft: GROUP_DIVIDER }} />
                      {visibleGroups.flatMap((g) =>
                        GROUP_COLUMNS[g].flatMap((col, colIndex) => {
                          const totalValue = col.total ? col.total(data.totals) : null;
                          const totalTd = (
                            <Table.Td
                              key={col.key}
                              style={{
                                textAlign: col.align === 'left' ? 'left' : 'right',
                                whiteSpace: 'nowrap',
                                ...(colIndex === 0 ? { borderLeft: GROUP_DIVIDER } : {}),
                              }}
                            >
                              {totalValue === null
                                ? ''
                                : formatNumber(
                                    totalValue,
                                    col.key === 'accidentRateOwn' ||
                                      col.key === 'accidentRateIndirect' ||
                                      col.key === 'accidentRateTotal'
                                      ? 2
                                      : 0
                                  )}
                            </Table.Td>
                          );
                          if (showDeltaColumns && col.renderDelta) {
                            return [totalTd, <Table.Td key={`${col.key}-delta`} />];
                          }
                          return [totalTd];
                        })
                      )}
                      <Table.Td style={{ borderLeft: GROUP_DIVIDER }} />
                    </Table.Tr>
                  </Table.Tfoot>
                </Table>
              </ScrollArea>
            </Card>
          </>
        )
      )}
    </Stack>
  );
}
